import type { DonationSchemeRecord } from "@project/types/donation-scheme";

const DEFAULT_PRESETS = [501, 1100, 2100, 5100, 11000, 25000];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=900&q=70&auto=format&fit=crop";

function parseAmount(value?: string | number | null): number {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }
  const num = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(num) ? num : 0;
}

export type DonationHubTile = {
  kind: "campaign";
  id: string;
  title: string;
  description: string | null;
  image: string | null;
  badge?: string;
  target?: number;
  collected?: number;
  categoryId: string;
  categoryName: string;
  dateStatus?: string;
};

export type DonationCheckoutCause = {
  kind: "campaign";
  id: string;
  title: string;
  description: string | null;
  image: string | null;
  target?: number;
  collected?: number;
  suggested: number[];
  min_amount: number;
  is_80g: boolean;
  allow_recurring: boolean;
  category_id?: string | null;
  scheme_id: number;
  donation_type: string;
  amount_type?: string;
  fixed_amount?: number;
  min_contribution?: number;
  max_contribution?: number;
};

function isLiveScheme(scheme: DonationSchemeRecord): boolean {
  const status = scheme.date_status?.toLowerCase();
  if (status === "past" || status === "closed" || status === "completed") {
    return false;
  }
  if (status === "live" || status === "active" || status === "upcoming") {
    return true;
  }
  return scheme.is_active !== false;
}

export function getSchemeImageUrl(scheme: DonationSchemeRecord): string | null {
  const banner = scheme.banner_image?.trim();
  return banner ?? null;
}

export function buildPresetAmounts(scheme: DonationSchemeRecord): number[] {
  if (scheme.amount_type === "fixed") {
    const fixed = parseAmount(scheme.fixed_amount);
    if (fixed > 0) {
      return [fixed];
    }
    return DEFAULT_PRESETS;
  }

  const min = parseAmount(scheme.min_contribution);
  const minAmount = min > 0 ? min : 100;
  const max = parseAmount(scheme.max_contribution);
  if (max > 0) {
    const inRange = DEFAULT_PRESETS.filter(
      amount => amount >= minAmount && amount <= max
    );
    if (inRange.length > 0) {
      return inRange;
    }
    return [minAmount, Math.round((minAmount + max) / 2), max].filter(
      (value, index, arr) => arr.indexOf(value) === index
    );
  }

  return DEFAULT_PRESETS.filter(amount => amount >= minAmount);
}

export function schemeToHubTile(scheme: DonationSchemeRecord): DonationHubTile {
  const goal = parseAmount(scheme.amount_needed);
  const collected = parseAmount(scheme.amount_collected);
  const live = isLiveScheme(scheme);

  return {
    kind: "campaign",
    id: String(scheme.id),
    title: scheme.title,
    description:
      scheme.description?.trim() ??
      scheme.sub_title?.trim() ??
      null,
    image: getSchemeImageUrl(scheme),
    badge: live && goal > 0 ? "Featured" : undefined,
    target: goal,
    collected,
    categoryId: String(scheme.category ?? ""),
    categoryName: scheme.category_name?.trim() ?? "General",
    dateStatus: scheme.date_status ?? undefined,
  };
}

export function schemeToCheckoutCause(
  scheme: DonationSchemeRecord
): DonationCheckoutCause {
  const suggested = buildPresetAmounts(scheme);
  const fixedMin = parseAmount(scheme.fixed_amount);
  const variableMin = parseAmount(scheme.min_contribution);
  const minAmount =
    scheme.amount_type === "fixed"
      ? fixedMin > 0
        ? fixedMin
        : (suggested[0] ?? 100)
      : variableMin > 0
        ? variableMin
        : 100;

  const goal = parseAmount(scheme.amount_needed);
  const collectedAmount = parseAmount(scheme.amount_collected);
  const fixedAmount = parseAmount(scheme.fixed_amount);
  const minContribution = parseAmount(scheme.min_contribution);
  const maxContribution = parseAmount(scheme.max_contribution);

  return {
    kind: "campaign",
    id: String(scheme.id),
    scheme_id: scheme.id,
    donation_type:
      scheme.type_of_donation_name?.trim() ??
      scheme.title?.trim() ??
      "General",
    title: scheme.title,
    description:
      scheme.description?.trim() ??
      scheme.sub_title?.trim() ??
      null,
    image: getSchemeImageUrl(scheme) ?? FALLBACK_IMAGE,
    target: goal > 0 ? goal : undefined,
    collected: collectedAmount > 0 ? collectedAmount : undefined,
    suggested,
    min_amount: minAmount,
    is_80g: true,
    allow_recurring: false,
    category_id: scheme.category ? String(scheme.category) : null,
    amount_type: scheme.amount_type,
    fixed_amount: fixedAmount > 0 ? fixedAmount : undefined,
    min_contribution: minContribution > 0 ? minContribution : undefined,
    max_contribution: maxContribution > 0 ? maxContribution : undefined,
  };
}

export function getHubAggregateStats(tiles: DonationHubTile[]) {
  const goal = tiles.reduce((sum, tile) => sum + (tile.target ?? 0), 0);
  const raised = tiles.reduce((sum, tile) => sum + (tile.collected ?? 0), 0);
  return {
    goal,
    raised,
    pct: goal > 0 ? Math.min(100, (raised / goal) * 100) : 0,
    drives: tiles.length,
  };
}
